import type { IntroConfig } from '../../types';
import { DomHelpers } from "@/utils/DomHelpers";

const DEFAULT_CONFIG: IntroConfig = {
  exitDelay: 1.4,
  loadTimeout: 5000,
};

/**
 * Controls the logo intro animation and scroll lock until exit.
 * Uses window.gsap (typed in types/globals.d.ts).
 */
export class IntroController {
  private readonly config: IntroConfig;
  private locked = false;
  private enterDone = false;
  private loaded: boolean;
  private exited = false;
  private exitDelayScheduled = false;
  private exitTimer: { kill?: () => void } | null = null;

  constructor(config: Partial<IntroConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.loaded = document.readyState === 'complete';
  }

  init(): void {
    const gsap = window.gsap;
    if (!gsap) return;

    const intro = DomHelpers.qs<HTMLElement>(document, '.no-intro');
    const logoBox = intro?.querySelector('.logo-box');
    const svg = logoBox?.querySelector('svg');
    const paths = svg ? DomHelpers.qsAll<SVGPathElement>(svg, 'path') : [];
    const hero =
      DomHelpers.qs<HTMLElement>(document, '.no-main-hero__inner') ||
      DomHelpers.qs<HTMLElement>(document, '.no-main-hero');

    if (!intro || !svg || paths.length === 0) return;

    const html = document.documentElement;
    const body = document.body;

    this.lock(body, html);

    gsap.set(paths, { opacity: 0, y: 30 });
    if (hero) gsap.set(hero, { yPercent: 6, opacity: 0 });
    gsap.set(intro, {
      clipPath: 'inset(0% 0% 0% 0%)',
      webkitClipPath: 'inset(0% 0% 0% 0%)',
    });

    const enterTL = gsap.timeline({ defaults: { ease: 'power3.out' } });
    enterTL
      .to(paths, { opacity: 1, y: 0, duration: 0.6, stagger: 0.08, delay: 0.15 })
      .add(() => {
        this.enterDone = true;
        this.tryExit();
      });

    const startExit = (): void => {
      if (this.exited) return;
      this.exited = true;
      if (this.exitTimer) {
        this.exitTimer.kill?.();
        this.exitTimer = null;
      }
      const tl = gsap.timeline({ defaults: { ease: 'power3.inOut' } });
      tl.to(paths, { y: -120, opacity: 0, stagger: 0.06, duration: 1.0 }, 0).to(
        intro,
        {
          clipPath: 'inset(0% 0% 100% 0%)',
          webkitClipPath: 'inset(0% 0% 100% 0%)',
          duration: 1.2,
        },
        0.05
      );
      if (hero) {
        tl.to(hero, { yPercent: 0, opacity: 1, duration: 1.25, ease: 'power3.out' }, 0.1);
      }
      tl.add(() => {
        intro.remove();
        setTimeout(() => this.unlock(body, html), 180);
      });
    };

    this.tryExit = (): void => {
      if (this.loaded && this.enterDone && !this.exited) {
        if (!this.exitDelayScheduled) {
          this.exitDelayScheduled = true;
          this.exitTimer = gsap.delayedCall(this.config.exitDelay, startExit);
        }
      }
    };

    if (!this.loaded) {
      window.addEventListener(
        'load',
        () => {
          this.loaded = true;
          this.tryExit();
        },
        { once: true }
      );
    } else {
      this.tryExit();
    }

    setTimeout(() => {
      this.loaded = true;
      this.tryExit();
    }, this.config.loadTimeout);
  }

  private tryExit!: () => void;

  private lock(body: HTMLElement, html: HTMLElement): void {
    if (this.locked) return;
    html.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.inset = '0';
    body.style.width = '100%';
    window.lenis?.stop?.();
    this.locked = true;
  }

  private unlock(body: HTMLElement, html: HTMLElement): void {
    if (!this.locked) return;
    body.style.position = '';
    body.style.inset = '';
    body.style.width = '';
    body.style.top = '';
    html.style.overflow = '';
    window.lenis?.start?.();
    this.locked = false;
  }
}
